import { Tag, TagMap } from '../types';

export const UNTAGGED_KEY = '__untagged__';

export const normalizeTagName = (name: string): string => name.trim().toLowerCase();

export const normalizeTagKey = (name?: string | null): string => {
  if (!name || !name.trim()) {
    return UNTAGGED_KEY;
  }
  return normalizeTagName(name);
};

export const flattenTags = (tags: Tag[]): Tag[] => {
  const result: Tag[] = [];
  const visit = (tag: Tag) => {
    result.push({ ...tag, children: tag.children ?? [] });
    (tag.children ?? []).forEach(visit);
  };
  tags.forEach(visit);
  return result;
};

export const getTopLevelTags = (tags: Tag[]): Tag[] => {
  const childNames = new Set<string>();
  tags.forEach((tag) => {
    (tag.children ?? []).forEach((child) => childNames.add(normalizeTagName(child.name)));
  });

  const seen = new Set<string>();
  return tags.filter((tag) => {
    const normalizedName = normalizeTagName(tag.name);
    if (childNames.has(normalizedName) || seen.has(normalizedName)) {
      return false;
    }

    seen.add(normalizedName);
    return true;
  });
};

export const buildTagMap = (tags: Tag[]): TagMap => {
  const map: TagMap = {};
  flattenTags(tags).forEach((tag) => {
    map[normalizeTagName(tag.name)] = { ...tag, children: tag.children ?? [] };
  });
  return map;
};

export const findTagByName = (tags: Tag[], tagName?: string | null): Tag | null => {
  if (!tagName || !tagName.trim()) {
    return null;
  }

  const normalized = normalizeTagName(tagName);
  const stack = [...tags];
  while (stack.length > 0) {
    const current = stack.pop()!;
    if (normalizeTagName(current.name) === normalized) {
      return current;
    }
    stack.push(...(current.children ?? []));
  }
  return null;
};

export const getDescendantTagNames = (tags: Tag[], parentTagName: string): Set<string> => {
  const parent = findTagByName(tags, parentTagName);
  if (!parent) {
    return new Set();
  }

  const names = new Set<string>();
  const queue: Tag[] = [parent];
  while (queue.length > 0) {
    const current = queue.shift()!;
    names.add(normalizeTagName(current.name));
    queue.push(...(current.children ?? []));
  }
  return names;
};
